import { Injectable } from '@angular/core';
import { UserRest } from './core.user.rest.service';
import { UserService } from './core.user.service';
import { UiService } from './core.ui.service';
import { GfTranslateService } from './core.translate.service';

export type ChatStatus = 'online' | 'away' | 'busy' | 'offline';

/**
 * The current user's chat status (online/away/busy/offline).
 *
 * One instance is shared by everything that shows or changes it -- the chat
 * toolbar picker and the user dropdown in the top menu -- so picking a status
 * in one place shows up in the others straight away.
 *
 * Offline is system-managed (the backend sets it once the user has left every
 * chat room), so it is never offered as a choice.
 */
@Injectable()
export class ChatStatusService {

    /** The choices a user may pick, in display order */
    public readonly choices: { status: ChatStatus, label: string }[] = [
        { status: 'online', label: 'Online' },
        { status: 'away', label: 'Away' },
        { status: 'busy', label: 'Busy' }
    ];

    /** Read directly from templates; online until proven otherwise */
    public status: ChatStatus = 'online';

    private loadPromise: Promise<ChatStatus> | null = null;
    private pendingSets = 0;
    /** Bumped on every change, so a slow load() can tell it's been overtaken */
    private version = 0;

    constructor(
        private userRest: UserRest,
        private userService: UserService,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService
    ) { }

    /**
     * Fetch the user's status from the server, once. Later calls reuse the
     * first answer, since chat polls keep it current from then on (see
     * sync()); `force` fetches it again for places with no chat open.
     */
    load(force = false): Promise<ChatStatus> {
        if (this.loadPromise && !force) {
            return this.loadPromise;
        }

        const unixName = this.userService.getUnixName();
        if (!this.userService.session.hasUser() || !unixName) {
            return Promise.resolve(this.status);
        }

        const startVersion = this.version;
        const promise: Promise<ChatStatus> = this.userRest.getChatStatus(unixName)
            .then(
                (status: any) => {
                    // A poll or a pick since this was asked for is newer
                    // news (e.g. the server setting online on entering a
                    // room while this was in flight).
                    if (this.version == startVersion) {
                        this.sync(status);
                    }
                    return this.status;
                },
                () => {
                    // Leave the status as it is; let the next caller try again.
                    this.loadPromise = null;
                    return this.status;
                }
            );
        this.loadPromise = promise;

        return promise;
    }

    /**
     * Pick a new status. The change shows at once and is rolled back if the
     * server refuses it.
     */
    set(status: ChatStatus): Promise<ChatStatus> {
        if (!this.choices.some((item) => item.status === status) || status == this.status) {
            return Promise.resolve(this.status);
        }

        const previous = this.status;
        this.status = status;
        this.version++;
        this.pendingSets++;

        return this.userRest.postChatStatus(this.userService.getUnixName(), status)
            .finally(() => this.pendingSets--)
            .then(
                () => this.status,
                () => {
                    this.status = previous;
                    this.version++;
                    this.uiService.message.showError(
                        this.gfTranslateService.translate('There was a problem changing your chat status'),
                        this.gfTranslateService.translate('Chat Status')
                    );
                    return this.status;
                }
            );
    }

    /**
     * Take a status reported by the server (the GET, or the current user's
     * own entry in a chat_users poll), which is how server-side changes --
     * forced online on entering a room, offline after leaving them all --
     * reach the pickers.
     */
    sync(status: any) {
        // A poll already in flight when the user picked a status would
        // otherwise flip the picker back to the old value for a moment.
        if (this.pendingSets > 0) {
            return;
        }

        if (['online', 'away', 'busy', 'offline'].includes(status)) {
            this.status = status;
            this.version++;
        }
    }

    isStatus(status: ChatStatus) {
        return this.status == status;
    }
}
